/* eslint-disable global-require */
import { Vue, Logger } from 'vue-infra';
import { has, get, replace, each, isArray } from 'lodash';
import Config from '@config/config';
import routes from '@core/router';
import {
  PRIVILEGES,
  FEATURES,
  WIDGET_PERMISSIONS_MAP,
  WORKSPACE_PARTIAL_VIEW_WIDGETS,
  SESSION_SETTINGS,
} from '@config/Consts';
import * as mixins from '@core/mixins/mixins';
import { registerComponents } from '@core/plugins/register-components';

const logger = Logger.getLogger('agentManagerDashboard');
const { sessionManager } = require('vue-infra');


const agentManagerDashboardVueApp = r => require.ensure([], () => r(require('./App.vue')), 'agentManagerDashboardVueApp');
const amwStore = r => require.ensure([], () => r(require('@core/store')), 'store');
const PortalVue = r => require.ensure([], () => r(require('portal-vue')), 'PortalVue');

if (window._) {
  window._.noConflict();
}

function loadInContext(filename) {
  return new Promise((resolve) => {
    if (filename === 'en-US') {
      // en-US is excluded from bundle-loader and pre-loaded by default
      // eslint-disable-next-line import/no-dynamic-require
      require([`@core/i18n/dictionaries/${filename}.json`], resolve);
    } else {
      // all other locals are loaded dynamically using the bundle-loader
      // eslint-disable-next-line import/no-dynamic-require
      require(`@core/i18n/dictionaries/${filename}.json`)((dict) => {
        resolve(dict);
      });
    }
  });
}

async function loadLocale(locale, fallbackLocale) {
  const local = {};
  // load custom local
  try {
    local.localResource = await loadInContext(locale);
  } catch (e) {
    logger.error(`fail to load dictionary ${locale}`);
  }
  // load fallback local
  if (locale !== fallbackLocale) {
    try {
      local.fallbackLocaleResource = await loadInContext(fallbackLocale);
    } catch (e) {
      logger.error(`fail to load dictionary ${fallbackLocale}`);
    }
  }
  return local;
}

const registerVueGlobalComponent = async () => {
  Vue.use(PortalVue, {
    portalName: 'amw-portal',
    portalTargetName: 'amw-portal-target',
  });

  // Register the component globally to get the Vue dev tools working.
  // load dictionaries only after the app load approved
  Vue.component(Config.applicationName, {
    name: Config.applicationName,
    template: '<App/>',
    components: { App: agentManagerDashboardVueApp },
  });

  // We use it to add this methods to all components
  Vue.mixin({
    computed: {
      emitter: mixins.emitter,
    },
    methods: {
      getMetricScoreWithUnit: mixins.getMetricScoreWithUnit,
      isDurationMetric: mixins.isDurationMetric,
      isArrayMetric: mixins.isArrayMetric,
      getConversationStatusClass: mixins.getConversationStatusClass,
      orderedCollection: mixins.orderedCollection,
      getStatusReasonText: mixins.getStatusReasonText,
      sendReportingSDE: mixins.sendReportingSDE,
      getPlaceholderSettings: mixins.getPlaceholderSettings,
      getGraphThemeSettings: mixins.getGraphThemeSettings,
    },
  });
  // Register global UI components
  registerComponents(Vue);
};
// TODO !!! refactor :: extract all these methods into Module.Initialise
const setManagersApiState = (store, manifestConfiguration) => {
  if (store && store.default && manifestConfiguration && has(manifestConfiguration, 'useNewManagerApi')) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_CHANGE_NEW_MANAGER_API_STATE], manifestConfiguration.useNewManagerApi, { root: true });
  }
};

const setResponseMetricsState = (store, manifestConfiguration) => {
  if (store && store.default && manifestConfiguration && has(manifestConfiguration, 'fetchResponseMetrics')) {
    store.default.dispatch(
      [store.ROOT_ACTION_PATHS.ROOT_ACTION_CHANGE_FETCH_RESPONSE_METRICS_STATE],
      manifestConfiguration.fetchResponseMetrics,
      { root: true }
    );
  }
};

const setEnableIntentsState = (store, manifestConfiguration) => {
  if (store && store.default && manifestConfiguration && has(manifestConfiguration, 'enableIntents')) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_CHANGE_ENABLE_INTENTS_STATE], manifestConfiguration.enableIntents, { root: true });
  }
};

const setEnableIntentsFilter = (store, manifestConfiguration) => {
  if (store && store.default && manifestConfiguration && has(manifestConfiguration, 'enableIntentsFilter')) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_CHANGE_ENABLE_INTENTS_FILTER_STATE], manifestConfiguration.enableIntentsFilter, { root: true });
  }
};

const setWorkspacePartialWidgets = (store, managerWorkspacePartialWidgets) => {
  if (managerWorkspacePartialWidgets && store && store.default) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_SET_WORKSPACE_PARTIAL_WIDGETS], { root: true });
  }
};

const setUseBotsGraphMock = (store, manifestConfiguration) => {
  if (manifestConfiguration && has(manifestConfiguration, 'useBotsGraphMock') && store && store.default) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_SET_USE_BOTS_GRAPH_MOCK], manifestConfiguration.useBotsGraphMock, { root: true });
  }
};

const setBotsGraphMock = (store, manifestConfiguration) => {
  if (manifestConfiguration && has(manifestConfiguration, 'botsGraphMock') && store && store.default) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_SET_BOTS_GRAPH_MOCK], manifestConfiguration.botsGraphMock, { root: true });
  }
};

const setShowBotGraph = (store, manifestConfiguration) => {
  if (manifestConfiguration && has(manifestConfiguration, 'showBotGraph') && store && store.default) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_SET_SHOW_BOT_GRAPH], manifestConfiguration.showBotGraph, { root: true });
  }
};

const updateStoreByContext = (store, config) => {
  setManagersApiState(store, config.manifestConfiguration);
  setResponseMetricsState(store, config.manifestConfiguration);
  setWorkspacePartialWidgets(store, config.useManagerWorkspacePartialWidgets);
  setEnableIntentsState(store, config.manifestConfiguration);
  setEnableIntentsFilter(store, config.manifestConfiguration);
  setUseBotsGraphMock(store, config.manifestConfiguration);
  setBotsGraphMock(store, config.manifestConfiguration);
  setShowBotGraph(store, config.manifestConfiguration);
};

const setAmwVersion = (store, manifestVersion) => {
  if (store && store.default && manifestVersion) {
    store.default.dispatch([store.ROOT_ACTION_PATHS.ROOT_ACTION_SET_AMW_VERSION], manifestVersion, { root: true });
  }
};

const canViewManagerWorkspace = () => {
  const viewWorkspacePrivilege = sessionManager.hasPrivilege(PRIVILEGES.VIEW_AGENT_MANAGER_WORKSPACE_PRIVILEGE);
  const viewWorkspacePrivilegeNew = sessionManager.hasPrivilege(PRIVILEGES.VIEW_AGENT_MANAGER_WORKSPACE_PRIVILEGE_NEW);
  const hasViewWorkspacePrivilege = viewWorkspacePrivilege || viewWorkspacePrivilegeNew;
  const hasViewWorkspaceFeature = sessionManager.getFeaturePropertyState(FEATURES.MANAGER_WORKSPACE);
  const hasMessagingFeature = sessionManager.getFeaturePropertyState(FEATURES.MESSAGING);
  return hasMessagingFeature && hasViewWorkspacePrivilege && hasViewWorkspaceFeature;
};

const canViewNAWByPrivilege = () => {
  let canView = false;

  const viewWorkspaceFeature = sessionManager.getFeaturePropertyState(FEATURES.NEW_AGENT_WORKSPACE);
  const viewWorkspacePrivilege = sessionManager.hasPrivilege(PRIVILEGES.VIEW_AGENT_WORKSPACE);
  const hasMessagingPrivilege = sessionManager.hasPrivilege(PRIVILEGES.VIEW_ACTIVE_CONNECTIONS);
  if (viewWorkspaceFeature && viewWorkspacePrivilege && hasMessagingPrivilege) {
    canView = true;
  }
  return canView;
};

const canViewNAWBySiteSetting = () => {
  const showNewAgentWorkspace = sessionManager.getAccountSettingValueByID(SESSION_SETTINGS.SHOW_NEW_AGENT_WORKSPACE, false);
  const hasMessagingPrivilege = sessionManager.hasPrivilege(PRIVILEGES.VIEW_ACTIVE_CONNECTIONS);
  const canView = (showNewAgentWorkspace === 'true') && hasMessagingPrivilege;
  return canView;
};

const canViewByEnforceNAWFeature = () => {
  const enforceNewAgentWorkspaceFeature = sessionManager.getFeaturePropertyState(FEATURES.ENFORCE_NEW_AGENT_WORKSPACE);
  const hasMessagingPrivilege = sessionManager.hasPrivilege(PRIVILEGES.VIEW_ACTIVE_CONNECTIONS);
  return enforceNewAgentWorkspaceFeature && hasMessagingPrivilege;
};

const LPASettingsForNAW = () => {
  let lpaSettings;
  if (sessionManager.isLPA()) {
    const newAgentWorkspaceForLPASettings = sessionManager.getAccountSettingByID(SESSION_SETTINGS.NEW_AGENT_WORKSPACE_LPA);
    const loginName = sessionManager.getLoginName();
    const lpaName = replace(loginName, 'LPA-', '');
    lpaSettings = get(newAgentWorkspaceForLPASettings, ['propertyValue', 'value', lpaName]);
  }
  return lpaSettings;
};

const isLPANotDisabled = () => get(LPASettingsForNAW(), 'newAgentWorkspace') !== 'false';

const isLPAWithNAWEnabled = () => get(LPASettingsForNAW(), 'newAgentWorkspace') === 'true';

const canViewPartialWidgetByPrivilege = () => {
  let allowed = false;
  each(WORKSPACE_PARTIAL_VIEW_WIDGETS, (widget) => {
    if (!allowed && get(WIDGET_PERMISSIONS_MAP, widget)
      && isArray(WIDGET_PERMISSIONS_MAP[widget])) {
      let allPermissionsMatch = true;
      each(WIDGET_PERMISSIONS_MAP[widget], (permission) => {
        allPermissionsMatch = allPermissionsMatch && sessionManager.hasPrivilege(permission);
      });
      allowed = allPermissionsMatch;
    }
  });
  return allowed;
};

const canViewPartialManagerWorkspace = (manifestConfiguration) => {
  const allowAgentsViewManagerWorkspace = manifestConfiguration.allowAgentsViewManagerWorkspace;
  const hasMessagingFeature = sessionManager.getFeaturePropertyState(FEATURES.MESSAGING);
  const hasAgentViewForAgentsInMWSFeature = sessionManager.getFeaturePropertyState(FEATURES.AGENT_VIEW_FOR_AGENTS);
  const canLPAViewNAW = isLPAWithNAWEnabled() && isLPANotDisabled();
  const viewPartialManagerWorkspace = allowAgentsViewManagerWorkspace && hasMessagingFeature &&
    hasAgentViewForAgentsInMWSFeature && canViewPartialWidgetByPrivilege() &&
    (canViewNAWByPrivilege() || canViewNAWBySiteSetting() || canViewByEnforceNAWFeature()
      || canLPAViewNAW);
  return viewPartialManagerWorkspace;
};

/**
 *
 * @param leConfig
 * @returns {Promise<{}>}
 * @description INIT ALL APPLICATION DEPENDENCIES
 * please notice that the order of functions in "applicationInit" is critical,
 * change with extra care.....
 */
const applicationInit = async (leConfig) => {
  let result = {};
  const appConfig = get(leConfig, 'config');
  const managerWorkspaceFullWidgets = canViewManagerWorkspace();
  const managerWorkspacePartialWidgets = canViewPartialManagerWorkspace(appConfig);
  if (managerWorkspaceFullWidgets || managerWorkspacePartialWidgets) {
    require('@assets/styles/style.scss');
    registerVueGlobalComponent();
    // We need developmentVersion to ensure we update site-setting property correctly,
    // as in dev mode the port is the version number
    const developmentVersion = get(appConfig, 'developmentVersion');
    const amwVersion = get(leConfig, 'version');
    await amwStore((store) => {
      updateStoreByContext(store, { manifestConfiguration: appConfig, useManagerWorkspacePartialWidgets: managerWorkspacePartialWidgets && !managerWorkspaceFullWidgets });
      setAmwVersion(store, developmentVersion || amwVersion);
    });
    const local = await loadLocale(leConfig.locale, leConfig.fallbackLocale || leConfig.defaultLocale); // leConfig.defaultLocale will deprecate at LE 10.8
    result = {
      i18nResource: local.localResource, // will deprecate at LE 10.8
      fallbackI18nResource: local.fallbackLocaleResource, // will deprecate at LE 10.8
      appLocaleResource: local.localResource,
      appFallbackLocaleResource: local.fallbackLocaleResource,
      routes,
      appId: Config.applicationName,
    };
  }
  return result;
};

const webpackPathInit = (appConfig) => {
// eslint-disable-next-line no-undef,camelcase
  __webpack_public_path__ = appConfig.publicPath;
};

export default {
  applicationInit,
  webpackPathInit,
  keepDOMRendered: true,
  config: {
    // A change in the 'id' property will require a code change
    id: 'agentManagerDashboard',
    external: true,
    moduleName: 'LEAgentManagerDashboardVue',
    folderName: 'amd-vue-wrapper',
    regionId: 'amd-vue',
    container: 'work-area-wrapper',
    template: 'workAreaItemNoLEClass',
    routePrefix: 'amd',
    personaMapping: 'agentManager',
    personas: ['agent', 'admin', 'messagingAgent', 'agentManager'],
    lazy: true,
    autostart: true,
    transition: true,
    switcher: {
      control: 'switcher-item-agent-manager-dashboard',
      fontIconName: 'ico-amd',
      index: 60,
      title: 'persona.agentManagerWorkspace.text',
      tooltip: 'persona.agentManagerWorkspace.tooltip',
    },
  },
};
